import { useState, useEffect } from "react";
import { Loader2, Users, Crown, Repeat } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { formatGs, formatDate, ORDER_STATUS } from "@/lib/format";

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("total_spent");
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)).finally(() => setLoading(false)); }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/customers/${id}`);
    setDetail(data);
  };

  const sorted = [...customers].sort((a, b) => {
    if (sort === "orders") return b.orders_count - a.orders_count;
    if (sort === "oldest") return (a.first_order || "").localeCompare(b.first_order || "");
    return b.total_spent - a.total_spent;
  });

  return (
    <div>
      <PageHeader title="Clientes" subtitle={`${customers.length} cliente(s) · ranking de clientes VIP`}
        action={
          <div className="flex gap-1.5 bg-white rounded-lg border border-slate-200 p-1">
            {[["total_spent", "Mayor gasto"], ["orders", "Más pedidos"], ["oldest", "Más antiguo"]].map(([k, l]) => (
              <button key={k} onClick={() => setSort(k)} className={`px-3 py-1.5 rounded-md text-xs font-medium ${sort === k ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100"}`} data-testid={`sort-${k}`}>{l}</button>
            ))}
          </div>
        } />

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
        : customers.length === 0 ? <EmptyState icon={Users} text="Sin clientes." />
        : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sorted.map((c, i) => (
              <button key={c.id} onClick={() => openDetail(c.id)} className="text-left bg-white rounded-xl border border-slate-200 p-5 hover:border-indigo-300 transition-colors" data-testid={`customer-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-pink-500 to-indigo-600 flex items-center justify-center text-white font-bold">{(c.name || c.whatsapp || "?").charAt(0).toUpperCase()}</div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate flex items-center gap-1">{c.name || "Sin nombre"} {i === 0 && sort === "total_spent" && <Crown className="h-4 w-4 text-amber-500" />}</p>
                    <p className="text-xs text-slate-400 truncate">{c.whatsapp || c.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div><p className="font-bold text-slate-900">{c.orders_count}</p><p className="text-xs text-slate-400">pedidos</p></div>
                  <div><p className="font-bold text-slate-900 text-sm">{formatGs(c.total_spent)}</p><p className="text-xs text-slate-400">gastado</p></div>
                  <div><p className="font-bold text-slate-900 text-sm">{formatGs(c.avg_ticket)}</p><p className="text-xs text-slate-400">ticket</p></div>
                </div>
                {c.recurrent && <span className="inline-flex items-center gap-1 mt-3 text-xs bg-pink-50 text-pink-600 px-2 py-0.5 rounded-full"><Repeat className="h-3 w-3" /> Cliente recurrente</span>}
              </button>
            ))}
          </div>
        )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.name || "Cliente"}</DialogTitle></DialogHeader>
              <div className="text-sm space-y-1.5">
                <div className="flex justify-between"><span className="text-slate-500">WhatsApp</span><span className="font-medium">{detail.whatsapp || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Email</span><span className="font-medium">{detail.email || "—"}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Instagram</span><span className="font-medium">@{detail.instagram}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Registro</span><span className="font-medium">{formatDate(detail.created_at)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Total pedidos</span><span className="font-medium">{detail.orders_count}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Total gastado</span><span className="font-medium">{formatGs(detail.total_spent)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Ticket medio</span><span className="font-medium">{formatGs(detail.avg_ticket)}</span></div>
                {detail.recurrent && <p className="text-pink-600 font-medium text-xs pt-1">✓ Cliente recurrente</p>}
              </div>
              <p className="font-semibold text-slate-800 mt-3 mb-1">Historial de pedidos</p>
              <div className="space-y-2">
                {detail.orders.map((o) => (
                  <div key={o.id} className="flex justify-between items-center bg-slate-50 rounded-lg p-2.5 text-sm">
                    <div><p className="font-medium">#{o.order_number} · {o.service_name}</p><p className="text-xs text-slate-400">{formatDate(o.created_at)}</p></div>
                    <div className="text-right"><p className="font-medium">{formatGs(o.total)}</p><span className={`text-xs px-2 py-0.5 rounded-full ${ORDER_STATUS[o.status]?.color}`}>{ORDER_STATUS[o.status]?.label}</span></div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
